import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, loginRequest } from "@/authConfig";
import { getLocalAuthToken, clearLocalAuthSession } from "@/localAuth";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL as string
});

apiClient.interceptors.request.use(async (config) => {
  // Email/password (non-SSO) sessions carry their own JWT and have no MSAL
  // account at all - checked first so they never touch MSAL's token cache.
  const localToken = getLocalAuthToken();
  if (localToken) {
    config.headers.Authorization = `Bearer ${localToken}`;
    return config;
  }

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return config;

  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    config.headers.Authorization = `Bearer ${result.accessToken}`;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Falls back to an interactive prompt (e.g. conditional access, expired consent).
      await msalInstance.acquireTokenRedirect(loginRequest);
    } else {
      throw error;
    }
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      // A local JWT rejected as 401 is expired/invalid - clear it so the app
      // falls back to the login screen, rather than redirecting to Microsoft
      // sign-in for a user who never had an MSAL session to begin with.
      if (getLocalAuthToken()) {
        clearLocalAuthSession();
      } else {
        msalInstance.loginRedirect(loginRequest);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
